/**
 * Centralized plan configuration for Penzu checkout
 *
 * Tier names used in this app and their API membership_package_name mappings:
 *   - pro: standard
 *   - promonthly: standardmonthly
 *   - proplus: proplus
 *   - proplusmonthly: proplusmonthly
 *   - propluslifetime: ProPlusLifetime
 */

// ============ Types ============

/** Tier identifiers used in URLs and internally */
export type PlanTier = 'pro' | 'promonthly' | 'proplus' | 'proplusmonthly' | 'propluslifetime';

/** Billing period - yearly tiers support multi-year (1-5), monthly is always 1 */
export type BillingPeriod = 'monthly' | 'yearly' | 'lifetime';

export interface PlanConfig {
  /** Tier identifier (used in URLs) */
  tier: PlanTier;
  /** API membership_package_name value */
  membershipPackageName: string;
  /** Display name for UI */
  displayName: string;
  /** Short name for buttons */
  shortName: string;
  /** Billing period type */
  billingPeriod: BillingPeriod;
  /** Base price (yearly or monthly rate) */
  basePrice: number;
  /** Price label suffix ("/ year", "/ month", or empty) */
  periodLabel: string;
  /** Marketing description */
  description: string;
  /** Feature list */
  features: string[];
  /** Feature list heading */
  featuresHeading: string;
  /** Show "Most Popular" badge */
  popular?: boolean;
  /** Discount for additional years (e.g., 0.5 = 50% off per extra year) */
  extraYearDiscount?: number;
  /** Whether this tier supports multi-year purchase (1-5 years) */
  supportsMultiYear: boolean;
}

// ============ Tier to API Mapping ============

export const TIER_TO_MEMBERSHIP_PACKAGE: Record<PlanTier, string> = {
  pro: 'standard',
  promonthly: 'standardmonthly',
  proplus: 'proplus',
  proplusmonthly: 'proplusmonthly',
  propluslifetime: 'ProPlusLifetime',
};

// ============ Plan Definitions ============

export const PLANS: Record<PlanTier, PlanConfig> = {
  pro: {
    tier: 'pro',
    membershipPackageName: 'standard',
    displayName: 'Penzu Pro',
    shortName: 'Pro',
    billingPeriod: 'yearly',
    basePrice: 19.99,
    periodLabel: '/ year',
    description: 'Make it yours with customization, plus pro-active features to keep you writing.',
    featuresHeading: 'Everything in Basic, plus:',
    features: [
      'Encryption locking',
      'Custom journal covers, fonts & colors',
      'Entry tagging',
      'PDF export',
      'Writing prompts',
      'Write via email (Penzu Post)',
    ],
    extraYearDiscount: 0.49974, // slightly less than 50% to get a round $10 price per year
    supportsMultiYear: true,
  },

  promonthly: {
    tier: 'promonthly',
    membershipPackageName: 'standardmonthly',
    displayName: 'Penzu Pro',
    shortName: 'Pro',
    billingPeriod: 'monthly',
    basePrice: 4.99,
    periodLabel: '/ month',
    description: 'Make it yours with customization, plus pro-active features to keep you writing.',
    featuresHeading: 'Everything in Basic, plus:',
    features: [
      'Encryption locking',
      'Custom journal covers, fonts & colors',
      'Entry tagging',
      'PDF export',
      'Writing prompts',
      'Write via email (Penzu Post)',
    ],
    supportsMultiYear: false,
  },

  proplus: {
    tier: 'proplus',
    membershipPackageName: 'proplus',
    displayName: 'Penzu Pro+',
    shortName: 'Pro+',
    billingPeriod: 'yearly',
    basePrice: 49.99,
    periodLabel: '/ year',
    description: 'The most powerful suite of tools for journaling.',
    featuresHeading: 'Everything in Pro, plus:',
    features: [
      'Premium handwritten fonts',
      'Fullscreen zen-mode',
      'Versions & entry recovery',
      'Premium support',
      'Military grade encryption locking',
    ],
    popular: true,
    extraYearDiscount: 0.499899, // slightly less than 50% to get a round $25 price per year
    supportsMultiYear: true,
  },

  proplusmonthly: {
    tier: 'proplusmonthly',
    membershipPackageName: 'proplusmonthly',
    displayName: 'Penzu Pro+',
    shortName: 'Pro+',
    billingPeriod: 'monthly',
    basePrice: 9.99,
    periodLabel: '/ month',
    description: 'The most powerful suite of tools for journaling.',
    featuresHeading: 'Everything in Pro, plus:',
    features: [
      'Premium handwritten fonts',
      'Fullscreen zen-mode',
      'Versions & entry recovery',
      'Premium support',
      'Military grade encryption locking',
    ],
    popular: true,
    supportsMultiYear: false,
  },

  propluslifetime: {
    tier: 'propluslifetime',
    membershipPackageName: 'ProPlusLifetime',
    displayName: 'Penzu Pro+ Lifetime',
    shortName: 'Pro+ Lifetime',
    billingPeriod: 'lifetime',
    basePrice: 199.99,
    periodLabel: '',
    description: 'The biggest upgrade you can make to your life, for life.',
    featuresHeading: 'Everything in Pro+, including:',
    features: ['One-time payment', 'No need to renew', '24hr support'],
    supportsMultiYear: false,
  },
};

// ============ Helper Functions ============

/**
 * Get a plan by its tier
 */
export function getPlanByTier(tier: PlanTier): PlanConfig {
  return PLANS[tier];
}

/**
 * Check if a string is a valid tier
 */
export function isValidTier(tier: string): tier is PlanTier {
  return tier in PLANS;
}

/**
 * Parse tier from URL parameter (case-insensitive)
 */
export function parseTierParam(param: string | null): PlanTier | undefined {
  if (!param) return undefined;
  const normalized = param.toLowerCase();
  if (isValidTier(normalized)) {
    return normalized;
  }
  return undefined;
}

/**
 * Parse period from URL parameter
 * Returns a number 1-5, defaults to 1
 */
export function parsePeriodParam(param: string | null): number {
  if (!param) return 1;
  const num = parseInt(param, 10);
  if (isNaN(num) || num < 1 || num > 5) return 1;
  return num;
}

/**
 * Calculate total price for a plan with multi-year option
 *
 * Pricing logic: Full price for year 1, then extraYearDiscount off for each additional year
 * Example with 50% discount: Year 1 = $50, Years 2-5 = $25 each
 *   - 1 year: $50
 *   - 2 years: $50 + $25 = $75
 *   - 3 years: $50 + $25 + $25 = $100
 */
export function calculateTotalPrice(tier: PlanTier, years: number = 1): number {
  const plan = PLANS[tier];

  // Lifetime and monthly don't support multi-year
  if (!plan.supportsMultiYear || years <= 1) {
    return plan.basePrice;
  }

  const discount = plan.extraYearDiscount ?? 0;
  const firstYearPrice = plan.basePrice;
  const additionalYearPrice = plan.basePrice * (1 - discount);
  const additionalYears = Math.min(years, 5) - 1;

  return firstYearPrice + additionalYearPrice * additionalYears;
}

/**
 * Calculate price per year for multi-year purchase
 */
export function calculatePricePerYear(tier: PlanTier, years: number = 1): number {
  const total = calculateTotalPrice(tier, years);
  return total / years;
}

/**
 * Get savings percentage for multi-year vs single year
 */
export function calculateSavingsPercent(tier: PlanTier, years: number): number {
  if (years <= 1) return 0;
  const plan = PLANS[tier];
  if (!plan.supportsMultiYear) return 0;

  const singleYearTotal = plan.basePrice * years;
  const multiYearTotal = calculateTotalPrice(tier, years);
  const savings = ((singleYearTotal - multiYearTotal) / singleYearTotal) * 100;

  return Math.round(savings);
}

/**
 * Format price for display
 */
export function formatPrice(price: number): string {
  return `$${price.toFixed(2)}`;
}

export function shortFormatPrice(price: number): string {
  return `$${price.toFixed(0)}`;
}

/**
 * Get yearly tiers for plan selector (when "Pay Yearly" is selected)
 */
export function getYearlyTiers(): PlanTier[] {
  return ['pro', 'proplus', 'propluslifetime'];
}

/**
 * Get monthly tiers for plan selector (when "Pay Monthly" is selected)
 */
export function getMonthlyTiers(): PlanTier[] {
  return ['promonthly', 'proplusmonthly', 'propluslifetime'];
}

/**
 * Get the corresponding yearly tier for a monthly tier (for toggle switching)
 */
export function getYearlyEquivalent(tier: PlanTier): PlanTier {
  switch (tier) {
    case 'promonthly':
      return 'pro';
    case 'proplusmonthly':
      return 'proplus';
    default:
      return tier;
  }
}

/**
 * Get the corresponding monthly tier for a yearly tier (for toggle switching)
 */
export function getMonthlyEquivalent(tier: PlanTier): PlanTier {
  switch (tier) {
    case 'pro':
      return 'promonthly';
    case 'proplus':
      return 'proplusmonthly';
    default:
      return tier;
  }
}
